import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import type {
  FeatureFlagKey,
  FeatureFlagsDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

import { useIsAuthenticated } from '@/stores/authStore';

// A flip reaches an open client on its next focus; persisted by the query
// persister like everything else, so an allowlisted user keeps it offline.
export const FEATURE_FLAGS_STALE_TIME_MS = 15 * 60 * 1000;

export function useFeatureFlagsQuery() {
  const api = useTypedApi();
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: queryKeys.featureFlags(),
    queryFn: async (): Promise<FeatureFlagsDto> => {
      const response =
        (await api.featureFlags.get()) as SuccessResponseDto<FeatureFlagsDto>;
      // Throw (rather than return null) so a failed refetch keeps the last
      // successful answer in `data` instead of overwriting it.
      if (!response?.data) {
        throw new Error('Feature flags response missing data');
      }
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: FEATURE_FLAGS_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/** Unknown means off: the flags hide work in progress, never core paths. */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { data } = useFeatureFlagsQuery();
  return data?.[key] ?? false;
}
